import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../components/tracker/Tracker.css';
import '../../components/tracker/PlayerDashboard.css';

export default function PlayerListPage() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [teamSwitchPlayer, setTeamSwitchPlayer] = useState(null);
  const [switchingTeam, setSwitchingTeam] = useState(false);

  useEffect(() => {
    async function fetchPlayers() {
      try {
        const res = await api.get('/analytics/players');
        setPlayers(res.data || []);
      } catch (err) {
        console.error('Failed to load players:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPlayers();
  }, []);

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await api.delete(`/analytics/players/${id}`);
      setPlayers((prev) => prev.filter((p) => p.id !== id));
      setDeleteId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  }

  async function handleTeamSwitch(player, teamId) {
    setSwitchingTeam(true);
    try {
      const updated = { ...player, currentTeamId: teamId };
      await api.put(`/analytics/players/${player.id}`, updated);
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === player.id ? { ...p, currentTeamId: teamId } : p
        )
      );
      setTeamSwitchPlayer(null);
    } catch (err) {
      console.error('Team switch failed:', err);
    } finally {
      setSwitchingTeam(false);
    }
  }

  function getCurrentTeam(player) {
    if (!player.teams || player.teams.length === 0) return null;
    return (
      player.teams.find((t) => t.id === player.currentTeamId) || player.teams[0]
    );
  }

  function pitcherBadge(player) {
    if (!player.isPitcher) return null;
    const label = player.isPitcherOnly ? 'PO' : 'P';
    return <span className="player-role-badge">{label}</span>;
  }

  return (
    <div className="dash-page">
      <div className="analytics-page-wrap">
        {/* Header */}
        <div className="analytics-page-header">
          <button className="analytics-back-btn" onClick={() => navigate('/')}>
            ← Home
          </button>
          <h1 className="analytics-page-title">My Players</h1>
          <button
            className="analytics-add-btn"
            onClick={() => navigate('/analytics/players/new')}
          >
            + Add Player
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="analytics-empty">Loading players…</div>
        ) : players.length === 0 ? (
          <div className="analytics-empty">
            <div className="analytics-empty-icon">⚾</div>
            <div className="analytics-empty-text">No players yet.</div>
            <button
              className="analytics-empty-btn"
              onClick={() => navigate('/analytics/players/new')}
            >
              Add Your First Player
            </button>
          </div>
        ) : (
          <div className="player-list">
            {players.map((player) => (
              <div
                key={player.id}
                className="player-card"
                onClick={() =>
                  navigate(`/analytics/players/${player.id}/dashboard`)
                }
                style={{ cursor: 'pointer' }}
              >
                <div className="player-card-main">
                  <div className="player-card-name">
                    {player.firstName} {player.lastName}
                    {pitcherBadge(player)}
                  </div>
                  <div className="player-card-meta">
                    <span className="player-sport-badge">{player.sport}</span>
                    {player.gradYear && (
                      <span className="player-meta-item">
                        '{String(player.gradYear).slice(-2)}
                      </span>
                    )}
                    {player.positions && player.positions.length > 0 && (
                      <span className="player-meta-item">
                        {player.positions.join(' · ')}
                      </span>
                    )}
                  </div>

                  {/* Current team */}
                  {(() => {
                    const ct = getCurrentTeam(player);
                    return ct ? (
                      <button
                        className="player-current-team-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (player.teams && player.teams.length > 1) {
                            setTeamSwitchPlayer(player);
                          }
                        }}
                      >
                        <span className="player-current-team-name">
                          {ct.name}
                        </span>
                        <span
                          className={`team-membership-badge ${ct.membership}`}
                        >
                          {ct.membership === 'full_time'
                            ? 'Full Time'
                            : 'Guest'}
                        </span>
                        {player.teams && player.teams.length > 1 && (
                          <span className="player-team-switch-hint">
                            ⇄ Switch
                          </span>
                        )}
                      </button>
                    ) : null;
                  })()}

                  {player.isPitcher &&
                    player.pitches &&
                    player.pitches.length > 0 && (
                      <div className="player-pitches-row">
                        {player.pitches.map((p) => (
                          <span key={p} className="player-pitch-chip">
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
                <div className="player-card-actions">
                  <button
                    className="player-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/analytics/players/${player.id}/dashboard`);
                    }}
                    title="View dashboard"
                  >
                    📊
                  </button>
                  <button
                    className="player-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/analytics/players/${player.id}/edit`);
                    }}
                    title="Edit player"
                  >
                    ✎
                  </button>
                  <button
                    className="player-action-btn danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(player.id);
                    }}
                    title="Delete player"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Start Tracking CTA */}
        {players.length > 0 && (
          <div className="analytics-track-cta">
            <button
              className="analytics-track-btn"
              onClick={() => navigate('/analytics/tracker')}
            >
              Start Tracking a Game →
            </button>
          </div>
        )}
      </div>

      {/* Team switcher modal */}
      {teamSwitchPlayer && (
        <div className="modal-overlay" onClick={() => setTeamSwitchPlayer(null)}>
          <div className="team-switch-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="team-switch-title">
              Switch Current Team
            </div>
            <div className="team-switch-subtitle">
              {teamSwitchPlayer.firstName}'s Teams
            </div>
            <div className="team-switch-list">
              {teamSwitchPlayer.teams.map((team) => {
                const isCurrent = team.id === teamSwitchPlayer.currentTeamId;
                return (
                  <button
                    key={team.id}
                    className={`team-switch-option ${isCurrent ? 'current' : ''}`}
                    onClick={() => handleTeamSwitch(teamSwitchPlayer, team.id)}
                    disabled={switchingTeam || isCurrent}
                  >
                    <div className="team-switch-option-info">
                      <div className="team-switch-option-name">{team.name}</div>
                      <div className="team-switch-option-meta">
                        {team.type}{team.season ? ` · ${team.season}` : ''}
                      </div>
                    </div>
                    <div className="team-switch-option-right">
                      <span className={`team-membership-badge ${team.membership}`}>
                        {team.membership === 'full_time' ? 'Full Time' : 'Guest'}
                      </span>
                      {isCurrent && (
                        <span className="team-switch-current-check">✓ Current</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              className="team-switch-cancel"
              onClick={() => setTeamSwitchPlayer(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Delete this player?</h3>
            <p>
              This will remove the player and all their game history from your
              dashboard. You can recover them within 3 days from Recently
              Deleted in your account settings.
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Player'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
